// Target attribution + whole-share plan: the FI page's "why these targets" and "how many shares".
//
// Mirrors two blocks of get_fixed_income_snapshot's payload (HelionBackend, 2026-09-14):
//   meta.target_attribution  fixed_income_logic._v3_attribution, completed by
//                            fixed_income_stickiness._build_target_attribution
//   meta.rebalance_plan      fi_rebalance_shares.build_fi_share_plan
// The backend does the reasoning and the arithmetic; every sentence in a step's `why` is written there.
// This module only arranges it for display and splits a rung's dollar gap into its causes. It never
// recomputes a target, a cap or a share count.

export interface AttributionStep {
  key: string;
  label: string;
  applied: boolean;
  state?: string | null;
  why: string;
  inputs?: Record<string, any>;
  weight_deltas: Record<string, number>;
  weights_after?: Record<string, number>;
}

export interface AttributionBase {
  key: string;
  label: string;
  applied: boolean;
  state?: string | null;
  why: string;
  inputs?: Record<string, any>;
  weights: Record<string, number>;
}

export interface PrincipalBreach {
  ticker: string;
  return_pct: number | null;
  limit_pct: number;
  market_value?: number | null;
  cost_basis?: number | null;
  reasons?: string[];
  cap_weight?: number | null;
  capped: boolean;
  previous_weight?: number | null;
  reallocated_weight?: number | null;
  recovery_to_lift_pct?: number | null;
  approx_yield_decline_to_lift_bps?: number | null;
  duration_years?: number | null;
  /** Zero-cap lockout (2026-09-15): the stop sold this rung to zero; it stays at its 0% cap until `free_from`. */
  lockout?: boolean;
  locked_since?: string | null;
  locked_through?: string | null;
  free_from?: string | null;
}

export interface DurationCapDetail {
  regime_key?: string | null;
  regime_cap_years?: number | null;
  enforced_cap_years?: number | null;
  cap_tightened_by?: string | null;
  capital_preservation_unknown?: boolean;
  detailed_state?: string | null;
  rate_trend_direction?: string | null;
}

export interface TargetAdoption {
  /** "engine_today" | "held_prior" | "hold_placeholder" */
  targets_source: string;
  /** True only when base + engine steps + principal defense reproduce the targets actually returned. */
  decomposable: boolean;
  reason?: string | null;
  accepted?: boolean | null;
  final_regime?: string | null;
  suggested_regime?: string | null;
  pending_regime?: string | null;
  pending_count?: number | null;
  confirm_days?: number | null;
  days_since_change?: number | null;
  min_days_between_flips?: number | null;
  hard_override?: boolean | null;
  held_targets_since_utc?: string | null;
}

export interface TargetAttribution {
  version?: number;
  engine?: string;
  hold?: boolean;
  hold_reason?: string | null;
  state?: string | null;
  regime?: string | null;
  rate_trend_direction?: string | null;
  computed_at_utc?: string | null;
  base?: AttributionBase | null;
  steps?: AttributionStep[];
  engine_weights?: Record<string, number> | null;
  final_weights?: Record<string, number>;
  principal_defense?: AttributionStep;
  duration?: {
    target_duration_base_years?: number | null;
    target_duration_before_budget_years?: number | null;
    target_duration_engine_years?: number | null;
    enforced_cap_years?: number | null;
    regime_cap_years?: number | null;
    regime_key?: string | null;
    cap_tightened_by?: string | null;
  } | null;
  duration_cap?: DurationCapDetail;
  adoption?: TargetAdoption;
  /** Set instead of the chain when the backend could not build it; the targets are unaffected. */
  error?: string;
}

export interface RebalanceRung {
  ticker: string;
  action: "buy" | "sell" | "hold" | "no_price";
  shares: number | null;
  price: number | null;
  price_source?: string | null;
  price_updated_utc?: string | null;
  gap_dollars: number;
  est_trade_dollars: number | null;
  unfilled_dollars?: number | null;
  held_quantity?: number;
  target_weight: number;
  current_weight: number;
  target_dollars?: number;
  current_market_value?: number;
  note?: string | null;
  /** Wash-sale buy-back hold (2026-09-15): set only on a BUY of a stop-sold rung inside its hold window. */
  wash_sale_hold?: WashSaleHold | null;
}

/** The tested strategy's trade gate (fi_principal_defense/simulate.py DRIFT_BAND), published by the backend. */
export interface ExecutionBand {
  available: boolean;
  l1_gap?: number;
  band?: number;
  trade?: boolean;
  /** "timer_run" | "live" | "live_fallback" */
  basis?: string;
  cash_term?: number;
  definition?: string;
  reason?: string;
}

export interface WashSaleHold {
  ticker: string;
  sale_detected_date: string;
  hold_through: string;
  buys_allowed_from: string;
  days: number;
  destinations: string;
  source?: string | null;
}

export interface TimerRunDifference {
  ticker: string;
  live: number;
  timer: number;
  delta: number;
}

/** The 15:15 MT timer run the checklist trades off (execution discipline, 2026-09-15). */
export interface TimerRun {
  available: boolean;
  reason?: string;
  run_utc?: string | null;
  session_date?: string | null;
  age_days?: number | null;
  label?: string | null;
  targets?: Record<string, number>;
  differences: TimerRunDifference[];
  material_weight?: number;
}

export interface RebalancePlan {
  available: boolean;
  reason?: string;
  sleeve_market_value?: number;
  min_trade_dollars?: number;
  sell_proceeds_est?: number;
  buy_cost_est?: number;
  residual_cash_est?: number;
  buys_trimmed_shares?: number;
  complete?: boolean;
  no_price_tickers?: string[];
  oldest_price_updated_utc?: string | null;
  rounding?: string;
  price_basis?: string;
  rungs: RebalanceRung[];
  execution_band?: ExecutionBand;
  /** "timer_run" (the 15:15 MT run's targets) | "live" | "live_fallback" (no run recorded yet) */
  basis?: string;
  wash_sale_holds?: Record<string, WashSaleHold>;
  held_buy_dollars?: number;
  timer_run_utc?: string | null;
}

/** A weight move under 0.05pp is rounding, not a decision. */
export const MATERIAL_WEIGHT = 0.0005;

export type Tone = "positive" | "warning" | "danger" | "info" | "neutral";

/** "v3:Calm | Calm | ..." -> "Calm"; "Defensive-FALLING" -> "Defensive-FALLING". */
export const regimeName = (label?: string | null): string =>
  (label ?? "").replace(/^v3:/, "").split("|")[0].trim() || "—";

export const materialMove = (step: AttributionStep): boolean =>
  Object.values(step.weight_deltas ?? {}).some((v) => Math.abs(v) >= MATERIAL_WEIGHT);

/** The engine's overlay steps in engine order. Rounding is shown only if it moved something material. */
export function engineSteps(attr?: TargetAttribution | null): AttributionStep[] {
  if (!attr || attr.error) return [];
  return (attr.steps ?? []).filter((s) => s.key !== "rounding" || materialMove(s));
}

/** Steps that actually changed a weight (never the base, never rounding). */
export function activeAdjustments(attr?: TargetAttribution | null): AttributionStep[] {
  if (!attr || attr.error) return [];
  const all = [...(attr.steps ?? []), ...(attr.principal_defense ? [attr.principal_defense] : [])];
  return all.filter((s) => s.key !== "rounding" && s.applied && materialMove(s));
}

export type StepStatus = "base" | "changed" | "not_triggered" | "off" | "no_weight_moved";

export function stepStatus(step: AttributionStep): StepStatus {
  if (step.key === "regime_base") return "base";
  if (step.applied && materialMove(step)) return "changed";
  if (/^Switched off/.test(step.why ?? "")) return "off";
  if (step.key === "principal_defense" && (step.inputs?.breaches?.length ?? 0) > 0) return "no_weight_moved";
  return "not_triggered";
}

/** "VCIT -24.0pp · SGOV +10.8pp · ..." for the material moves, largest first. */
export function formatMoves(deltas?: Record<string, number>, limit = 6): string {
  return Object.entries(deltas ?? {})
    .filter(([, v]) => Math.abs(v) >= MATERIAL_WEIGHT)
    .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]) || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([k, v]) => `${k} ${v > 0 ? "+" : "-"}${Math.abs(v * 100).toFixed(1)}pp`)
    .join(" · ");
}

export interface GapCause {
  key: string;
  label: string;
  dollars: number;
}

/**
 * Split one rung's dollar gap into what caused it.
 *
 *   gap = (final − current) × MV = (base − current) × MV  +  Σ step deltas × MV
 *
 * The first term is drift: how far the book already sits from the regime base. Steps under
 * `minDollars` are folded into it, so the causes always sum to the gap. Returns null whenever the
 * chain does not describe the targets actually returned (held prior targets, a data hold, an error):
 * splitting a held target by an un-adopted suggestion's steps would invent a reason.
 */
export function gapCauses(
  attr: TargetAttribution | null | undefined,
  ticker: string,
  currentWeight: number,
  sleeveMarketValue: number,
  minDollars = 500,
): GapCause[] | null {
  if (!attr || attr.error || !attr.base || !attr.adoption?.decomposable || !(sleeveMarketValue > 0)) return null;
  const causes: GapCause[] = [];
  let drift = ((attr.base.weights?.[ticker] ?? 0) - currentWeight) * sleeveMarketValue;
  const steps = [...(attr.steps ?? []), ...(attr.principal_defense ? [attr.principal_defense] : [])];
  for (const s of steps) {
    const dollars = (s.weight_deltas?.[ticker] ?? 0) * sleeveMarketValue;
    if (s.key !== "rounding" && Math.abs(dollars) >= minDollars) causes.push({ key: s.key, label: s.label, dollars });
    else drift += dollars;
  }
  if (causes.length === 0 || Math.abs(drift) >= 1) {
    causes.push({ key: "drift", label: `Drift from the ${regimeName(attr.state)} base`, dollars: drift });
  }
  return causes.sort((a, b) => Math.abs(b.dollars) - Math.abs(a.dollars));
}

export interface AdoptionSummary {
  tone: Tone;
  pill: string;
  text: string;
}

/** One plain-English sentence on whether these are today's engine targets, and what changed them. */
export function adoptionSummary(attr?: TargetAttribution | null): AdoptionSummary | null {
  if (!attr) return null;
  if (attr.error) {
    return {
      tone: "danger",
      pill: "Explanation unavailable",
      text: `The step-by-step explanation could not be built (${attr.error}). The targets themselves are unaffected.`,
    };
  }
  const a = attr.adoption;
  const since = a?.held_targets_since_utc ? ` (adopted ${a.held_targets_since_utc.slice(0, 10)})` : "";
  if (attr.hold || a?.reason === "v3_hold_data_unavailable") {
    return {
      tone: "warning",
      pill: "Holding prior targets",
      text:
        `Holding the last adopted targets${since}: the regime engine's inputs are unavailable ` +
        `(${attr.hold_reason ?? a?.reason ?? "unknown"}), so there is no engine reasoning for today. ` +
        "Only the holdings stop below was re-checked.",
    };
  }
  if (a && !a.decomposable) {
    const held = regimeName(a.final_regime);
    const suggested = regimeName(a.suggested_regime);
    if (a.reason === "pending_confirmation") {
      return {
        tone: "warning",
        pill: "Holding prior targets",
        text:
          `Holding the last adopted ${held} targets${since}. Today's engine suggests ${suggested}, which is adopted after ` +
          `${a.confirm_days ?? "?"} confirming trading days (${a.pending_count ?? 0} so far). The engine steps below describe ` +
          "that suggestion, not the targets the checklist trades to.",
      };
    }
    if (a.reason === "cooldown_block") {
      return {
        tone: "warning",
        pill: "Holding prior targets",
        text:
          `Holding the last adopted ${held} targets${since}. Today's engine suggests ${suggested}, but the last regime change ` +
          `was ${a.days_since_change ?? "?"} day(s) ago, inside the ${a.min_days_between_flips ?? "?"}-day cooldown. The engine ` +
          "steps below describe that suggestion, not the targets the checklist trades to.",
      };
    }
    return {
      tone: "warning",
      pill: "Holding prior targets",
      text: `Holding the last adopted targets${since} (${a.reason ?? "held"}). The engine steps below are not what the checklist trades to.`,
    };
  }
  const regime = regimeName(attr.state);
  const adjustments = activeAdjustments(attr);
  if (adjustments.length === 0) {
    return {
      tone: "positive",
      pill: "No adjustments",
      text: `Today's targets are the ${regime} regime base. Every overlay and the holdings stop was checked; none changed a weight.`,
    };
  }
  return {
    tone: "warning",
    pill: `${adjustments.length} adjustment${adjustments.length === 1 ? "" : "s"} active`,
    text:
      `Today's targets start from the ${regime} regime base, then ${adjustments.map((s) => s.label).join(", ")} ` +
      `changed the weights. The ${regime} label comes from the credit gate alone, so it does not change when an adjustment fires.`,
  };
}

/** Explains a governing duration cap that differs from the configured regime cap; null when it does not. */
export function capNote(
  cap?: {
    enforced?: number | null;
    regime?: number | null;
    tightenedBy?: string | null;
    unknown?: boolean | null;
    regimeKey?: string | null;
  } | null,
): string | null {
  if (!cap || cap.enforced == null) return null;
  if (cap.tightenedBy && cap.regime != null) {
    const mode = cap.tightenedBy.replace(/^capital_preservation_/, "").replace(/_/g, "-");
    return `Regime cap ${cap.regime.toFixed(2)}y tightened to ${cap.enforced.toFixed(2)}y by capital preservation (${mode}).`;
  }
  if (cap.unknown) {
    return (
      `${cap.regimeKey ?? "Regime"} cap ${cap.enforced.toFixed(2)}y as configured; any capital-preservation tightening ` +
      "today is unknown while the engine's inputs are unavailable."
    );
  }
  return null;
}

const wholeDollars = (n?: number | null) => `$${Math.round(Math.abs(n ?? 0)).toLocaleString("en-US")}`;

/** "Sell 29,812 sh @ $82.40 ≈ $2,456,509" — the order line for one rung, ignoring any wash-sale hold. */
export function sharesLineRaw(rung: RebalanceRung): string {
  if (rung.action === "no_price") return "No IBKR mark (not held): size it from a TWS quote";
  if (rung.action === "hold" || rung.shares == null || rung.price == null) return "Within tolerance";
  const verb = rung.action === "buy" ? "Buy" : "Sell";
  return `${verb} ${rung.shares.toLocaleString("en-US")} sh @ $${rung.price.toFixed(2)} ≈ ${wholeDollars(rung.est_trade_dollars)}`;
}

/** The checklist line: a held BUY reads as the hold (sells are never held). */
export function sharesLine(rung: RebalanceRung): string {
  const hold = rung.wash_sale_hold;
  if (rung.action === "buy" && hold) {
    return (
      `Wash-sale hold until ${hold.hold_through} (buys allowed from ${hold.buys_allowed_from}): leave proceeds in ` +
      `${hold.destinations}. CIO tax judgment; confirm with tax adviser.`
    );
  }
  return sharesLineRaw(rung);
}

const pctOne = (v?: number | null) => (v == null || Number.isNaN(v) ? "—" : `${(v * 100).toFixed(1)}%`);

/** Whether the tested strategy would trade, in one headline. Null when the backend published no band. */
export function bandHeadline(band?: ExecutionBand | null): { trade: boolean; text: string } | null {
  if (!band || !band.available || band.l1_gap == null || band.band == null) return null;
  const limit = `${+(band.band * 100).toFixed(2)}%`;
  return band.trade
    ? { trade: true, text: `Outside the execution band: trade to target (gap ${pctOne(band.l1_gap)} vs the ${limit} band).` }
    : {
        trade: false,
        text: `Within the ${limit} execution band: no trade needed (tested strategy would not trade). Gap ${pctOne(band.l1_gap)}.`,
      };
}

/** "Intraday targets differ from the 15:15 MT run (VCIT 29.8% now vs 6.0% at the run). ..." or null. */
export function timerRunBanner(run?: TimerRun | null): string | null {
  if (!run?.available || !run.differences?.length) return null;
  const shown = run.differences
    .slice(0, 4)
    .map((d) => `${d.ticker} ${pctOne(d.live)} now vs ${pctOne(d.timer)} at the run`)
    .join("; ");
  const more = run.differences.length > 4 ? `; +${run.differences.length - 4} more` : "";
  return `Intraday targets differ from the 15:15 MT run (${shown}${more}). Trade off the timer run; intraday changes are information.`;
}

export function planBasisNote(plan?: RebalancePlan | null, run?: TimerRun | null): string | null {
  if (!plan) return null;
  if (plan.basis === "timer_run" && run?.available) {
    const age = run.age_days != null && run.age_days > 0 ? ` (${run.age_days} day${run.age_days === 1 ? "" : "s"} ago)` : "";
    return `Checklist basis: the 15:15 MT run of ${run.session_date ?? "—"}${age}. Intraday refreshes are information; the next run updates this list.`;
  }
  if (plan.basis === "live_fallback") return "Checklist basis: live targets, because no 15:15 MT run has been recorded yet.";
  return null;
}

/** One line on the dollars a wash-sale hold leaves in the safe destinations; null when nothing is held. */
export function heldBuySummary(plan?: RebalancePlan | null): string | null {
  const holds = Object.values(plan?.wash_sale_holds ?? {});
  if (!plan || holds.length === 0 || !(plan.held_buy_dollars && plan.held_buy_dollars > 0)) return null;
  const names = holds.map((h) => `${h.ticker} (sale detected ${h.sale_detected_date}, hold through ${h.hold_through})`).join(", ");
  return (
    `Wash-sale hold: ${names}. Leave about ${wholeDollars(plan.held_buy_dollars)} of the ${holds[0].destinations} sells in place: ` +
    "they would fund the held buy. The hold starts when the 15:15 run detects the sale, which can be a day after the trade. " +
    "CIO tax judgment; not tax advice."
  );
}

export function planSummary(plan?: RebalancePlan | null): string | null {
  if (!plan) return null;
  if (!plan.available) return `Share counts unavailable: ${plan.reason ?? "unknown reason"}`;
  let text =
    `Whole shares at the last IBKR mark: sells raise ≈ ${wholeDollars(plan.sell_proceeds_est)}, ` +
    `buys cost ≈ ${wholeDollars(plan.buy_cost_est)}, residual cash ≈ ${wholeDollars(plan.residual_cash_est)}.`;
  if (plan.buys_trimmed_shares) {
    text += ` ${plan.buys_trimmed_shares} buy share${plan.buys_trimmed_shares === 1 ? "" : "s"} trimmed so the buys never cost more than the sells raise.`;
  }
  if (plan.complete === false && plan.no_price_tickers?.length) {
    text += ` No mark for ${plan.no_price_tickers.join(", ")}: size from a TWS quote.`;
  }
  return text;
}
